import { useCallback, useMemo } from 'react';

import { useSearching } from '../../hooks';
import { Autocomplete, AutocompleteProps } from '../../ui';
import { combineStrings, currencyData, getCurrencyByCode, isDefinedString } from '../../utils';

type CurrencyAutocompleteProps = Omit<AutocompleteProps<string>, 'options'> & {
  defaultCurrencyCode?: string
};

export function CurrencyAutocomplete({ defaultCurrencyCode, selected, onChange, ...other }: CurrencyAutocompleteProps) {
  const { search, updateSearch } = useSearching();

  const matchesSearch = useCallback(
    (value: string) =>
      !isDefinedString(search) ||
      value.toLowerCase().replace(/\s+/g, '').includes(search.toLowerCase().replace(/\s+/g, '')),
    [search]
  );

  const currencies = useMemo(
    () => currencyData.filter((x) => matchesSearch(x.code) || matchesSearch(x.currency)),
    [matchesSearch]
  );

  const orderedCurrencies = useMemo(
    () => currencies.sort((a, b) => (a.code > b.code ? 1 : -1)).sort((x) => (x.code === defaultCurrencyCode ? -1 : 1)),
    [currencies, defaultCurrencyCode]
  );

  const currencyCodes = useMemo(() => orderedCurrencies.map((x) => x.code), [orderedCurrencies]);

  const getLabel = useCallback(
    (currencyCode?: string) => combineStrings(' – ', currencyCode, getCurrencyByCode(currencyCode)?.currency) ?? '',
    []
  );

  return (
    <Autocomplete<string>
      selected={selected}
      options={currencyCodes}
      getSelectedLabel={getLabel}
      getOptionLabel={getLabel}
      onInput={updateSearch}
      onChange={(newSelected) => onChange(isDefinedString(newSelected) ? newSelected : '')}
      {...other}
    />
  );
}
